/**
 * v135 — Share modal.
 *
 * Mobile-first slide-up modal that previews the OG card, lets the user toggle
 * privacy options (label / photo), and exposes 3 actions:
 *
 *   1. Open share sheet (navigator.share with the PNG file attached) — primary
 *      mobile action. iMessage, Slack, IG DMs, etc. all support `files`.
 *   2. Copy link — falls back when share sheet isn't available, or for users
 *      who want to paste into something specific.
 *   3. Download PNG — secondary action for users who want the image on disk.
 *
 * Why createPortal: every modal in this codebase MUST escape any transformed
 * ancestor (per the modal-portal memory rule). Sliding-up modals tend to live
 * inside transformed wrappers and get position:fixed trapped.
 *
 * No PII by default: label + photo toggles default OFF. The user has to opt in.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Share2, Link2, Download, X, Loader2, Check } from 'lucide-react';
import { ShareCardSVG } from '../ShareCardSVG';
import { svgToPngBlob, downloadSvgAsPng } from '../svgToPng';
import { buildShareUrl } from '../shareEncoding';
import type { ShareSnapshot, SharePrivacyOptions } from '../types';
import { DEFAULT_PRIVACY } from '../types';

interface ShareModalProps {
  open: boolean;
  snapshot: ShareSnapshot;
  onClose: () => void;
  /** Storefront origin for the public share URL (default: location.origin). */
  origin?: string;
}

type ActionState = 'idle' | 'busy' | 'done' | 'error';

export function ShareModal({ open, snapshot, onClose, origin }: ShareModalProps) {
  const [privacy, setPrivacy] = useState<SharePrivacyOptions>(DEFAULT_PRIVACY);
  const [labelInput, setLabelInput] = useState(snapshot.customLabel ?? '');
  const [shareState, setShareState] = useState<ActionState>('idle');
  const [copyState, setCopyState] = useState<ActionState>('idle');
  const [downloadState, setDownloadState] = useState<ActionState>('idle');
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  // Sync if parent passes a fresh snapshot with a different label
  useEffect(() => {
    setLabelInput(snapshot.customLabel ?? '');
  }, [snapshot.customLabel, open]);

  // Resolve origin once — falls back to the current location
  const resolvedOrigin = useMemo(() => {
    if (origin) return origin;
    if (typeof window !== 'undefined' && window.location) return window.location.origin;
    return 'https://sparrowexpeditions.com';
  }, [origin]);

  // Decorate the snapshot with the live label override for the preview
  const renderedSnapshot: ShareSnapshot = useMemo(() => {
    if (!privacy.includeLabel) return { ...snapshot, customLabel: undefined };
    return { ...snapshot, customLabel: labelInput.trim() || undefined };
  }, [snapshot, privacy.includeLabel, labelInput]);

  const shareUrl = useMemo(
    () => buildShareUrl(resolvedOrigin, renderedSnapshot),
    [resolvedOrigin, renderedSnapshot],
  );

  // v135.2 hotfix — Web Share API capability probe.
  //
  // Two conditions must hold for the iOS share sheet to appear:
  //   1. The Permissions Policy in the iframe's `allow` attribute must
  //      include `web-share`. Without it, Safari may silently fall through
  //      to the system's default share intent — which on a phone with FB
  //      Messenger installed routes images straight to Messenger. The fix
  //      is paired with the section file's `allow="…; web-share"` update.
  //   2. `navigator.canShare({ files: [...] })` must return true. iOS Safari
  //      17+ returns true; older versions and locked-down iframes return false.
  //
  // We also detect the iframe-without-web-share case explicitly so we can
  // show a useful error if the section file deploy hasn't propagated yet.
  const canShareFiles = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    const nav = navigator as Navigator & {
      canShare?: (data: { files?: File[] }) => boolean;
    };
    if (typeof nav.canShare !== 'function') return false;
    try {
      const probe = new File([new Blob()], 'probe.png', { type: 'image/png' });
      return nav.canShare({ files: [probe] });
    } catch {
      return false;
    }
  }, []);

  // Permissions-Policy probe — explicitly checks whether the iframe is allowed
  // to call navigator.share. Returns null on browsers that don't support the
  // FeaturePolicy / PermissionsPolicy API. When false, we know calls will
  // reject with NotAllowedError and we surface a clearer message.
  const webShareAllowed = useMemo<boolean | null>(() => {
    if (typeof document === 'undefined') return null;
    const doc = document as Document & {
      featurePolicy?: { allowsFeature: (name: string) => boolean };
      permissionsPolicy?: { allowsFeature: (name: string) => boolean };
    };
    const policy = doc.featurePolicy ?? doc.permissionsPolicy;
    if (!policy || typeof policy.allowsFeature !== 'function') return null;
    try {
      return policy.allowsFeature('web-share');
    } catch {
      return null;
    }
  }, []);

  const handleShare = async () => {
    setShareState('busy');
    setErrMsg(null);
    try {
      if (!svgRef.current) throw new Error('Share card not ready');
      const blob = await svgToPngBlob(svgRef.current);
      // CRITICAL: must be a File, not a Blob — iOS Safari rejects Blob payloads
      // on the share sheet and falls through to a system intent.
      const file = new File([blob], 'sparrow-share.png', {
        type: 'image/png',
        lastModified: Date.now(),
      });
      const nav = navigator as Navigator & {
        share?: (d: object) => Promise<void>;
        canShare?: (d: { files?: File[] }) => boolean;
      };
      // Re-probe with the actual file we're about to share — the lazy
      // canShareFiles probe used an empty Blob; a real PNG may fail differently
      // on edge-case browsers.
      const canShareNow =
        typeof nav.canShare === 'function' &&
        nav.canShare({ files: [file] });

      if (!canShareNow || typeof nav.share !== 'function') {
        // Fall back to clipboard URL copy — never silently route to a
        // foreign share intent.
        await navigator.clipboard.writeText(shareUrl);
        setShareState('done');
        setTimeout(() => setShareState('idle'), 2000);
        return;
      }

      await nav.share({
        files: [file],
        title: 'Sparrow Expeditions · The Forge',
        text: privacy.includeLabel && labelInput
          ? `${labelInput} · sparrowexpeditions.com`
          : 'Built with The Forge · sparrowexpeditions.com',
        url: shareUrl,
      });
      setShareState('done');
      setTimeout(() => setShareState('idle'), 2000);
    } catch (err) {
      const e = err as Error;
      // AbortError = user cancelled the share sheet — that's not a failure
      if (e?.name === 'AbortError') {
        setShareState('idle');
        return;
      }
      // NotAllowedError = iframe lacks web-share Permissions Policy. Fall
      // back to copy-link so the user has *something* useful, and surface a
      // clear message so we can tell theme-deploy drift apart from a real
      // share failure.
      if (e?.name === 'NotAllowedError' || webShareAllowed === false) {
        try {
          await navigator.clipboard.writeText(shareUrl);
          setShareState('done');
          setErrMsg('iOS share sheet is gated by the storefront — link copied instead.');
          setTimeout(() => {
            setShareState('idle');
            setErrMsg(null);
          }, 4000);
          return;
        } catch {
          /* fall through */
        }
      }
      setShareState('error');
      setErrMsg(e?.message ?? 'Share failed');
    }
  };

  const handleCopy = async () => {
    setCopyState('busy');
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyState('done');
      setTimeout(() => setCopyState('idle'), 2000);
    } catch (err) {
      setCopyState('error');
      setErrMsg((err as Error)?.message ?? 'Copy failed');
    }
  };

  const handleDownload = async () => {
    setDownloadState('busy');
    try {
      if (!svgRef.current) throw new Error('Share card not ready');
      await downloadSvgAsPng(svgRef.current, 'sparrow-share.png');
      setDownloadState('done');
      setTimeout(() => setDownloadState('idle'), 2000);
    } catch (err) {
      setDownloadState('error');
      setErrMsg((err as Error)?.message ?? 'Download failed');
    }
  };

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Share"
      className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-lg sm:mx-4 bg-zinc-950 border border-zinc-800 rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[4px] text-ember-400">
              Share · {snapshot.variant}
            </p>
            <h3 className="text-lg font-semibold text-zinc-100 mt-1">
              {snapshot.variant === 'build' && 'Share this build'}
              {snapshot.variant === 'session' && 'Share this session'}
              {snapshot.variant === 'compare' && 'Share this comparison'}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-md hover:bg-zinc-800 text-zinc-400"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Preview */}
        <div className="p-4">
          <div className="rounded-md overflow-hidden border border-zinc-800 bg-black">
            <ShareCardSVG
              snapshot={renderedSnapshot}
              showLabel={privacy.includeLabel}
              showPhoto={privacy.includePhoto}
              svgRef={svgRef}
            />
          </div>
          <p className="mt-2 text-[11px] text-zinc-500 font-mono uppercase tracking-[2px]">
            1200 × 630 · ~150 KB png
          </p>
        </div>

        {/* Privacy options */}
        <div className="px-4 pb-3 space-y-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={privacy.includeLabel}
              onChange={(e) => setPrivacy((p) => ({ ...p, includeLabel: e.target.checked }))}
              className="mt-1 accent-ember-500"
            />
            <span className="flex-1">
              <span className="block text-sm text-zinc-200">Add a label</span>
              <span className="block text-xs text-zinc-500">
                Initials or a short tag, like "Elk rig" or "Sat AM."
              </span>
            </span>
          </label>
          {privacy.includeLabel && (
            <input
              type="text"
              value={labelInput}
              onChange={(e) => setLabelInput(e.target.value.slice(0, 60))}
              placeholder="Elk rig"
              className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-md text-sm text-zinc-100 placeholder-zinc-500 focus:border-ember-500 outline-none"
            />
          )}
          {snapshot.variant === 'session' && snapshot.photoDataUri && (
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={privacy.includePhoto}
                onChange={(e) => setPrivacy((p) => ({ ...p, includePhoto: e.target.checked }))}
                className="mt-1 accent-ember-500"
              />
              <span className="flex-1">
                <span className="block text-sm text-zinc-200">Include target photo</span>
                <span className="block text-xs text-zinc-500">
                  Off by default — your backyard / range may be in the frame.
                </span>
              </span>
            </label>
          )}
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-zinc-800 grid grid-cols-1 sm:grid-cols-3 gap-2">
          <button
            type="button"
            onClick={handleShare}
            disabled={shareState === 'busy'}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-ember-500 text-zinc-900 rounded-md font-semibold disabled:opacity-60"
          >
            {shareState === 'busy' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : shareState === 'done' ? (
              <Check className="w-4 h-4" />
            ) : (
              <Share2 className="w-4 h-4" />
            )}
            {canShareFiles ? 'Open share sheet' : 'Copy link'}
          </button>
          <button
            type="button"
            onClick={handleCopy}
            disabled={copyState === 'busy'}
            className="flex items-center justify-center gap-2 px-4 py-3 border border-zinc-700 text-zinc-200 rounded-md hover:bg-zinc-900"
          >
            {copyState === 'done' ? (
              <Check className="w-4 h-4 text-emerald-400" />
            ) : (
              <Link2 className="w-4 h-4" />
            )}
            {copyState === 'done' ? 'Copied' : 'Copy link'}
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloadState === 'busy'}
            className="flex items-center justify-center gap-2 px-4 py-3 border border-zinc-700 text-zinc-200 rounded-md hover:bg-zinc-900"
          >
            {downloadState === 'busy' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : downloadState === 'done' ? (
              <Check className="w-4 h-4 text-emerald-400" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Download PNG
          </button>
        </div>

        {errMsg && (
          <div className="px-4 pb-4 text-xs text-rose-300">
            {errMsg}
          </div>
        )}

        {/* Footnote */}
        <div className="px-4 pb-5 border-t border-zinc-800 pt-3">
          <p className="text-[11px] text-zinc-500 leading-relaxed">
            Sparrow doesn't include your name, location, or photos by default.
            The shared link points to a public Forge page that lets the
            recipient clone the {snapshot.variant} into their own setup.
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
